/**
 * leadPortalAnalyticsService.js
 *
 * Frontend service for the Lead Portal report page.
 * - fetchLeadPortalAnalytics → calls getLeadPortalAnalytics Cloud Function
 * - fetchLeadsPortalActivity → Firestore queries for all leads with portal users
 */

import { httpsCallable } from 'firebase/functions';
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db, functions } from '../firebase';

/**
 * Fetch GA4 aggregated analytics for the Start portal.
 *
 * @param {object} options
 * @param {string} [options.startDate='30daysAgo'] - GA4 date expression
 * @param {string} [options.endDate='today']
 * @returns {Promise<object>} result from getLeadPortalAnalytics Cloud Function
 */
export async function fetchLeadPortalAnalytics({ startDate = '30daysAgo', endDate = 'today' } = {}) {
  const fn = httpsCallable(functions, 'getLeadPortalAnalytics', { timeout: 30000 });
  const result = await fn({ startDate, endDate });
  return result.data;
}

/**
 * Fetch all lead accounts that have signed up for the Start portal,
 * joined with their portal user activity.
 *
 * Returns an array of objects:
 * {
 *   accountId, company, name, email,
 *   portalSignupDate,
 *   portalUserId, lastLogin, loginCount, startFlowStep,
 *   freeResourceClaimed
 * }
 */
export async function fetchLeadsPortalActivity() {
  // 1. Load all lead accounts
  const accountsSnap = await getDocs(
    query(collection(db, 'accounts'), where('type', '==', 'Leads'))
  );
  const accounts = accountsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  // 2. Load all portal users
  const usersSnap = await getDocs(collection(db, 'users'));
  const usersByAccountId = {};
  const usersByEmail = {};

  usersSnap.docs.forEach(d => {
    const data = d.data();
    if (data.accountId) {
      usersByAccountId[data.accountId] = { id: d.id, ...data };
    }
    if (data.email) {
      usersByEmail[data.email.toLowerCase().trim()] = { id: d.id, ...data };
    }
  });

  // 3. Join each lead to its portal user
  return accounts.map(account => {
    const email = (account.email || account.contact?.email || '').toLowerCase().trim();
    const portalUser =
      usersByAccountId[account.id] ||
      (email ? usersByEmail[email] : null);

    if (!portalUser) {
      return {
        accountId: account.id,
        company: account.company || account.name || account.id,
        email,
        portalSignupDate: account.portalSignupDate || null,
        portalUserId: null,
        lastLogin: null,
        loginCount: 0,
        startFlowStep: null,
        freeResourceClaimed: false,
      };
    }

    const lastLogin = portalUser.lastLogin?.toDate?.()
      ?? (portalUser.lastLogin ? new Date(portalUser.lastLogin) : null);
    const loginHistory = Array.isArray(portalUser.loginHistory)
      ? portalUser.loginHistory
      : [];
    const startFlow = portalUser.startFlowProgress || {};
    // currentStep > 1 only implies a claim on the legacy 5-step flow, where step 1 was
    // "Claim Free Resource". On the current flow (stepScheme set) claiming is explicit.
    const freeResourceClaimed =
      startFlow.freeResourceClaimed === true ||
      startFlow.freeResourceClaimed === 'true' ||
      (startFlow.stepScheme !== 'v2-no-free-resource' && startFlow.currentStep > 1);

    return {
      accountId: account.id,
      company: account.company || account.name || account.id,
      email,
      portalSignupDate: account.portalSignupDate || null,
      portalUserId: portalUser.id,
      lastLogin,
      loginCount: loginHistory.length,
      startFlowStep: startFlow.currentStep ?? null,
      freeResourceClaimed,
    };
  });
}
